import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

interface QuillEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  modules?: Record<string, any>;
  formats?: string[];
  theme?: 'snow' | 'bubble';
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export interface QuillEditorRef {
  getEditor: () => Quill | null;
}

const QuillEditor = forwardRef<QuillEditorRef, QuillEditorProps>(
  (
    {
      value = '',
      onChange,
      onKeyDown,
      modules,
      formats,
      theme = 'snow',
      placeholder,
      readOnly = false,
      className,
      style
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<Quill | null>(null);
    const onChangeRef = useRef(onChange);
    const isInternalChange = useRef(false);

    // Keep onChange ref current without re-initializing editor
    onChangeRef.current = onChange;

    useImperativeHandle(ref, () => ({
      getEditor: () => editorRef.current
    }));

    // Initialize Quill once
    useEffect(() => {
      if (!containerRef.current || editorRef.current) return;

      const editorDiv = document.createElement('div');
      containerRef.current.appendChild(editorDiv);

      const quill = new Quill(editorDiv, {
        theme,
        modules: modules || { toolbar: false },
        formats: formats || [],
        placeholder,
        readOnly
      });

      // Set initial content
      if (value) {
        quill.root.innerHTML = value;
      }

      quill.on('text-change', () => {
        isInternalChange.current = true;
        const html = quill.root.innerHTML;
        onChangeRef.current?.(html === '<p><br></p>' ? '' : html);
      });

      editorRef.current = quill;

      return () => {
        editorRef.current = null;
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      };
    }, []); // Only run once

    // Sync external value changes
    useEffect(() => {
      if (isInternalChange.current) {
        isInternalChange.current = false;
        return;
      }
      const editor = editorRef.current;
      if (editor && value !== editor.root.innerHTML) {
        const selection = editor.getSelection();
        editor.root.innerHTML = value || '';
        if (selection) {
          try {
            editor.setSelection(selection);
          } catch {
            // Selection may be out of bounds after content change
          }
        }
      }
    }, [value]);

    // Sync readOnly
    useEffect(() => {
      editorRef.current?.enable(!readOnly);
    }, [readOnly]);

    // Attach keydown listener
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || !onKeyDown) return;
      editor.root.addEventListener('keydown', onKeyDown);
      return () => {
        editor.root.removeEventListener('keydown', onKeyDown);
      };
    }, [onKeyDown]);

    return <div ref={containerRef} className={className} style={style} />;
  }
);

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;
